import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { SetupTask, SetupTasksService } from '../../../../core/services/setup-tasks.service';
import { PhoneSetupService } from '../../../../core/services/phone-setup.service';

const STATE_KEY_PREFIX = 'contemplate.setupIsland.expanded.';
const SUCCESS_MS = 3500;
/** Gutter kept clear of the sidebar / viewport edge. */
const GUTTER = 16;
/** Right-edge Messages / Journal tabs. */
const RIGHT_TABS = 64;
/** Phones: the round Journal button sits bottom-right. */
const RIGHT_FAB = 80;
const PHONE_MAX = 768;

/**
 * Floating "Finish setting up" island: a compact pill at the bottom centre of the main content area
 * that expands upward into the checklist. It stays while any task is pending.
 */
@Component({
  selector: 'app-setup-tasks-box',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './setup-tasks-box.component.html',
  styleUrls: ['./setup-tasks-box.component.scss']
})
export class SetupTasksBoxComponent implements OnInit, OnDestroy {
  tasks: SetupTask[] = [];
  expanded = false;
  routeAllowed = true;
  dialogOpen = false;
  showSuccess = false;
  announcement = '';
  /** Horizontal bounds (px from each viewport edge) of the area the island is centred in. */
  left = GUTTER;
  right = GUTTER;
  url = '';

  @ViewChild('toggleBtnRef') toggleBtn?: ElementRef<HTMLButtonElement>;
  @ViewChild('panelTitle') panelTitle?: ElementRef<HTMLElement>;

  private hadPending = false;
  private successTimer: ReturnType<typeof setTimeout> | null = null;
  private observer?: ResizeObserver;
  private observed: Element | null = null;
  private raf = 0;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private setup: SetupTasksService,
    private phoneSetup: PhoneSetupService,
    private router: Router,
    private host: ElementRef<HTMLElement>,
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) {}

  ngOnInit(): void {
    this.checkRoute();
    this.expanded = this.readExpanded();
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.checkRoute();
        // The layout (and its sidebar) mounts after navigation.
        setTimeout(() => this.layout(), 0);
        setTimeout(() => this.layout(), 400);
      });
    this.setup.tasks$.pipe(takeUntil(this.destroy$)).subscribe((tasks) => this.onTasks(tasks));
    this.phoneSetup.dialogOpen$.pipe(takeUntil(this.destroy$)).subscribe((open) => {
      this.dialogOpen = open;
      if (open) this.expanded = false;
      this.cdr.markForCheck();
    });

    if (typeof ResizeObserver !== 'undefined') {
      // Sidebar expand/collapse animates its width; follow it without re-entering the zone per frame.
      this.zone.runOutsideAngular(() => {
        this.observer = new ResizeObserver(() => this.scheduleLayout());
        this.observer.observe(document.body);
      });
    }
    this.layout();
  }

  ngOnDestroy(): void {
    if (this.successTimer) clearTimeout(this.successTimer);
    cancelAnimationFrame(this.raf);
    this.observer?.disconnect();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ---------- derived ----------

  get pendingCount(): number {
    return this.tasks.filter((t) => !t.done).length;
  }

  get doneCount(): number {
    return this.tasks.length - this.pendingCount;
  }

  get visible(): boolean {
    return this.routeAllowed && !this.dialogOpen && (this.pendingCount > 0 || this.showSuccess);
  }

  get progressPercent(): number {
    return this.tasks.length ? Math.round((this.doneCount / this.tasks.length) * 100) : 0;
  }

  get whatsappPending(): boolean {
    return this.tasks.some((t) => t.id === 'whatsapp' && !t.done);
  }

  get ctaLabel(): string {
    return this.whatsappPending ? 'Verify' : 'Set up';
  }

  /** One-liner for the collapsed pill, by what is missing and where the user is. */
  get copy(): string {
    if (!this.whatsappPending) return 'Set your country to finish setup';
    const u = this.url;
    if (u.startsWith('/coaches') || u.startsWith('/coaching')) {
      return 'Verify WhatsApp so your coaches can reach you';
    }
    if (u.startsWith('/pursuits') || u.startsWith('/hobbies')) return 'Verify WhatsApp for your Hobbies coach';
    if (u.startsWith('/mind-fitness')) return 'Verify WhatsApp for your Mind Fitness coach';
    return 'Finish setup · verify your WhatsApp';
  }

  get toggleLabel(): string {
    const n = this.pendingCount;
    return `Finish setting up, ${n} ${n === 1 ? 'task' : 'tasks'} left. ${this.expanded ? 'Collapse' : 'Expand'} checklist`;
  }

  // ---------- actions ----------

  verify(): void {
    // Every pending task currently shares one dialog; run the first pending task's action.
    this.tasks.find((t) => !t.done)?.action();
  }

  toggle(): void {
    this.setExpanded(!this.expanded, true);
  }

  collapse(returnFocus = false): void {
    this.setExpanded(false, returnFocus);
  }

  trackById(_: number, t: SetupTask): string {
    return t.id;
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.expanded && this.visible) this.collapse(true);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.expanded) return;
    if (!this.host.nativeElement.contains(event.target as Node)) this.collapse();
  }

  // ---------- internals ----------

  private setExpanded(value: boolean, manageFocus: boolean): void {
    if (this.expanded === value) return;
    const hadFocusInside = this.host.nativeElement.contains(document.activeElement);
    this.expanded = value;
    this.writeExpanded(value);
    this.cdr.detectChanges();
    if (value && manageFocus) {
      this.panelTitle?.nativeElement.focus();
    } else if (!value && (manageFocus || hadFocusInside)) {
      this.toggleBtn?.nativeElement.focus();
    }
  }

  private onTasks(tasks: SetupTask[]): void {
    const prev = this.tasks;
    this.tasks = tasks;
    const pending = tasks.filter((t) => !t.done).length;

    const finished = tasks.filter((t) => t.done && prev.some((p) => p.id === t.id && !p.done));
    if (finished.length && pending > 0) {
      this.announcement = `${finished.map((t) => t.label).join(', ')}: complete.`;
    }

    if (pending > 0) {
      this.hadPending = true;
      this.showSuccess = false;
    } else if (this.hadPending && tasks.length) {
      this.hadPending = false;
      this.showSuccess = true;
      this.expanded = false;
      this.announcement = 'All set. Your setup is complete.';
      if (this.successTimer) clearTimeout(this.successTimer);
      this.successTimer = setTimeout(() => {
        this.showSuccess = false;
        this.cdr.markForCheck();
      }, SUCCESS_MS);
    }
    this.cdr.markForCheck();
  }

  private checkRoute(): void {
    const full = this.router.url.split('?')[0].toLowerCase();
    this.url = full;
    this.routeAllowed = !(
      full === '/' ||
      full.startsWith('/auth') ||
      full.startsWith('/onboarding') ||
      full.includes('checkout')
    );
  }

  private scheduleLayout(): void {
    cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(() => this.layout());
  }

  /** Centre over the main content: right of the sidebar (any state), clear of the right-edge tabs. */
  private layout(): void {
    const vw = window.innerWidth;
    let left = GUTTER;
    let right = RIGHT_FAB;
    if (vw > PHONE_MAX) {
      const sidebar = document.querySelector('app-sidebar');
      if (sidebar && sidebar !== this.observed) {
        this.observed = sidebar;
        this.observer?.observe(sidebar);
      }
      const r = sidebar?.getBoundingClientRect();
      if (r && r.width > 0 && r.right > 0) left = Math.round(r.right) + GUTTER;
      right = RIGHT_TABS;
    }
    if (left !== this.left || right !== this.right) {
      this.zone.run(() => {
        this.left = left;
        this.right = right;
        this.cdr.markForCheck();
      });
    }
  }

  private key(): string {
    return STATE_KEY_PREFIX + (this.setup.userId ?? 'anon');
  }

  private readExpanded(): boolean {
    try {
      return localStorage.getItem(this.key()) === '1';
    } catch {
      return false;
    }
  }

  private writeExpanded(value: boolean): void {
    try {
      localStorage.setItem(this.key(), value ? '1' : '0');
    } catch {
      /* storage unavailable */
    }
  }
}
